import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Textarea, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Popover, PopoverContent, PopoverTrigger, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from '@cargomatic/ui';
import { addHubSchema } from './schemas';
import { useYards } from './hooks/useYards';
import { ApolloProvider } from '@apollo/client';
import { YardClient } from 'utils/YardAPIUtils';
import addresser from 'addresser';
import { ChevronsUpDown, Check } from 'lucide-react';
import LocationAutosuggest from 'components/site/location-autosuggest';
export const AddHubModal = ({ isOpen, onClose, onSubmit, defaultSpecialInstructions, }) => {
    // Provider needs to be above any hook usage; we delegate to a child component
    return (React.createElement(ApolloProvider, { client: YardClient },
        React.createElement(AddHubModalContent, { isOpen: isOpen, onClose: onClose, onSubmit: onSubmit, defaultSpecialInstructions: defaultSpecialInstructions })));
};
function AddHubModalContent({ isOpen, onClose, onSubmit, defaultSpecialInstructions, }) {
    const { yards, isLoading: loadingYards } = useYards({ isOpen });
    const AnyCommandItem = CommandItem;
    const { register, handleSubmit, watch, setValue, reset, setError, clearErrors, formState: { errors }, } = useForm({
        defaultValues: {
            hubbingType: 'pre_pull',
            yardId: '',
            name: '',
            street: '',
            city: '',
            state: '',
            zip: '',
            specialInstructions: defaultSpecialInstructions || '',
        },
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [yardOpen, setYardOpen] = React.useState(false);
    const [yardSearchValue, setYardSearchValue] = React.useState('');
    const [isCreatingYard, setIsCreatingYard] = React.useState(false);
    const [customYardName, setCustomYardName] = React.useState('');
    // Named handlers to replace large inline lambdas
    const selectExistingYard = (value) => {
        setIsCreatingYard(false);
        setCustomYardName('');
        handleYardChange(value);
        setYardOpen(false);
        setYardSearchValue('');
    };
    const handleExistingYardMouseDown = (value) => (e) => {
        e.preventDefault();
        selectExistingYard(value);
    };
    const closeOnCustomNameSelect = () => {
        setYardOpen(false);
    };
    const setCustomLocationName = (raw) => {
        const value = (raw || '').trim();
        if (!value)
            return;
        setIsCreatingYard(true);
        setCustomYardName(value);
        setValue('yardId', '', { shouldDirty: true, shouldValidate: true });
        setValue('name', value, { shouldDirty: true, shouldValidate: true });
        setValue('street', '', { shouldDirty: true });
        setValue('city', '', { shouldDirty: true });
        setValue('state', '', { shouldDirty: true });
        setValue('zip', '', { shouldDirty: true });
        setYardOpen(false);
        setYardSearchValue('');
        clearErrors(['street', 'city', 'state', 'zip']);
    };
    // Reset when modal closes
    React.useEffect(() => {
        // When opening, initialize special instructions from defaultSpecialInstructions
        if (isOpen) {
            setValue('specialInstructions', defaultSpecialInstructions || '', {
                shouldDirty: false,
                shouldValidate: false,
            });
            return;
        }
        if (!isOpen) {
            reset({
                hubbingType: 'pre_pull',
                yardId: '',
                name: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                specialInstructions: '',
            });
            clearErrors(['street', 'city', 'state', 'zip', 'hubbingType']);
        }
    }, [isOpen, reset, clearErrors, setValue, defaultSpecialInstructions]);
    const handleFormSubmit = async (data) => {
        try {
            // Mirror AddLegModal: validate with Zod manually and map issues to RHF errors
            const result = addHubSchema.safeParse(data);
            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    const pathJoined = (issue.path || []).join('.');
                    if (pathJoined) {
                        const fieldPath = pathJoined;
                        setError(fieldPath, { type: 'manual', message: issue.message });
                    }
                });
                return;
            }
            setIsSubmitting(true);
            const { street = '', city = '', state = '', zip = '' } = data;
            const addressLine = [
                street.trim(),
                [city, state].filter(Boolean).join(', ').trim(),
            ]
                .filter(Boolean)
                .join(', ');
            const address = `${addressLine}${zip ? ` ${zip}` : ''}`.trim();
            await onSubmit(Object.assign(Object.assign({}, data), { address }));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const selectedYard = React.useMemo(() => {
        const id = watch('yardId');
        const idStr = id != null ? String(id) : '';
        return yards.find((y) => String(y.id) === idStr) || null;
    }, [yards, watch('yardId')]);
    function handleYardChange(yardId) {
        const id = yardId || '';
        setValue('yardId', id);
        // Only exit custom-yard mode when selecting a real yard id
        if (id) {
            setIsCreatingYard(false);
            setCustomYardName('');
            setValue('name', '', { shouldDirty: true, shouldValidate: true });
        }
        // If empty selection, clear address fields
        if (!id) {
            setValue('street', '');
            setValue('city', '');
            setValue('state', '');
            setValue('zip', '');
            return;
        }
        const yard = yards.find((y) => String(y.id) === id);
        if (!yard || !yard.address) {
            setValue('street', '');
            setValue('city', '');
            setValue('state', '');
            setValue('zip', '');
            return;
        }
        try {
            const parsed = addresser.parseAddress(yard.address);
            setValue('street', parsed.addressLine1 || '');
            setValue('city', parsed.placeName || '');
            setValue('state', parsed.stateAbbreviation || '');
            setValue('zip', parsed.zipCode || '');
            clearErrors(['street', 'city', 'state', 'zip']);
        }
        catch (_err) {
            setValue('street', '');
            setValue('city', '');
            setValue('state', '');
            setValue('zip', '');
            setError('street', {
                type: 'manual',
                message: 'Unable to parse yard address. Please select a different yard.',
            });
        }
    }
    return (React.createElement(Dialog, { open: isOpen, onOpenChange: (open) => {
            if (!open) {
                reset({
                    hubbingType: 'pre_pull',
                    yardId: '',
                    name: '',
                    street: '',
                    city: '',
                    state: '',
                    zip: '',
                    specialInstructions: '',
                });
                clearErrors(['street', 'city', 'state', 'zip', 'hubbingType']);
                setIsCreatingYard(false);
                setCustomYardName('');
                onClose();
            }
        } },
        React.createElement(DialogContent, { className: "max-w-md max-h-[85vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Add Hub"),
                React.createElement(DialogDescription, null, "Add a yard location. This will split the shipment into two legs.")),
            React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit), className: "grid gap-4 py-4" },
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "yardId" }, "Yard"),
                    React.createElement(Popover, { open: yardOpen, onOpenChange: (open) => {
                            setYardOpen(open);
                            if (!open)
                                setYardSearchValue('');
                        } },
                        React.createElement(PopoverTrigger, { asChild: true },
                            React.createElement(Button, { type: "button", variant: "outline", role: "combobox", "aria-expanded": yardOpen, className: "w-full justify-between font-normal bg-transparent", id: "yardId", disabled: loadingYards || isSubmitting },
                                React.createElement("span", { className: watch('yardId') || isCreatingYard
                                        ? ''
                                        : 'text-muted-foreground' }, isCreatingYard && customYardName
                                    ? customYardName
                                    : selectedYard
                                        ? `${selectedYard.nameInternal || selectedYard.id}`
                                        : loadingYards
                                            ? 'Loading yards...'
                                            : 'Select a yard'),
                                React.createElement(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" }))),
                        React.createElement("input", Object.assign({ type: "hidden" }, register('yardId'))),
                        React.createElement(PopoverContent, { className: "w-[500px] p-0", side: "bottom", align: "start" },
                            React.createElement(Command, null,
                                React.createElement(CommandInput, { onValueChange: setYardSearchValue, placeholder: "Search yards by name or address...", className: "h-9" }),
                                React.createElement(CommandList, { className: "max-h-[240px] overflow-auto" },
                                    React.createElement(CommandGroup, null,
                                        isCreatingYard && customYardName && (React.createElement(AnyCommandItem, { key: "__custom_name__", value: customYardName, index: -1, item: customYardName, onSelect: closeOnCustomNameSelect },
                                            React.createElement("div", { className: "flex items-center gap-2", onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    closeOnCustomNameSelect();
                                                } },
                                                React.createElement(Check, { className: "mr-2 h-4 w-4 opacity-100" }),
                                                React.createElement("span", null, customYardName)))),
                                        yards
                                            .filter((y) => {
                                            const q = (yardSearchValue || '').toLowerCase();
                                            if (!q)
                                                return true;
                                            const name = (y.nameInternal || '').toLowerCase();
                                            const addr = (y.address || '').toLowerCase();
                                            const idStr = String(y.id).toLowerCase();
                                            return (name.includes(q) ||
                                                addr.includes(q) ||
                                                idStr.includes(q));
                                        })
                                            .map((y, i) => {
                                            const value = String(y.id);
                                            const label = `${y.nameInternal || value}`;
                                            const isSelected = watch('yardId') === value;
                                            return (React.createElement(AnyCommandItem, { key: value, value: label, index: i, item: y, onSelect: () => selectExistingYard(value) },
                                                React.createElement("div", { className: "flex items-center gap-2", onMouseDown: handleExistingYardMouseDown(value) },
                                                    React.createElement(Check, { className: `mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}` }),
                                                    React.createElement("span", null, label))));
                                        }),
                                        !loadingYards &&
                                            yards.filter((y) => {
                                                const q = (yardSearchValue || '').toLowerCase();
                                                if (!q)
                                                    return true;
                                                const name = (y.nameInternal || '').toLowerCase();
                                                const addr = (y.address || '').toLowerCase();
                                                const idStr = String(y.id).toLowerCase();
                                                return (name.includes(q) ||
                                                    addr.includes(q) ||
                                                    idStr.includes(q));
                                            }).length === 0 && (React.createElement(CommandEmpty, null,
                                            React.createElement("div", { className: "px-2" },
                                                React.createElement(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full justify-start", onClick: () => setCustomLocationName(yardSearchValue) },
                                                    "Set location name \"",
                                                    yardSearchValue,
                                                    "\""))))))))),
                    errors.yardId && (React.createElement("p", { className: "text-xs text-red-500" }, errors.yardId.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "hubbingType" }, "Hub Type"),
                    React.createElement(Select, { value: watch('hubbingType'), onValueChange: (value) => setValue('hubbingType', value), disabled: isSubmitting },
                        React.createElement(SelectTrigger, { id: "hubbingType", className: "w-full" },
                            React.createElement(SelectValue, { placeholder: "Select hub type" })),
                        React.createElement(SelectContent, null,
                            React.createElement(SelectItem, { value: "pre_pull" }, "Pre-Pull"),
                            React.createElement(SelectItem, { value: "stop_off" }, "Stop-Off"),
                            React.createElement(SelectItem, { value: "yard_diversion" }, "Yard Diversion"))),
                    errors.hubbingType && (React.createElement("p", { className: "text-xs text-red-500" }, errors.hubbingType.message))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, null, "Yard Address"),
                    React.createElement("div", { className: "grid grid-cols-1 gap-3" },
                        React.createElement("input", Object.assign({ type: "hidden" }, register('street'))),
                        React.createElement("input", Object.assign({ type: "hidden" }, register('city'))),
                        React.createElement("input", Object.assign({ type: "hidden" }, register('state'))),
                        React.createElement("input", Object.assign({ type: "hidden" }, register('zip'))),
                        React.createElement("div", { className: "grid gap-1" },
                            React.createElement(Label, { htmlFor: "yardAddress", className: "text-xs" }, "Address"),
                            React.createElement("div", { className: "relative" },
                                React.createElement(LocationAutosuggest, { id: "yardAddress", className: "form-field", value: (watch('street') || '').trim(), placeholderText: "START_TYPING_TO_SEARCH_FOR_A_LOCATION", searchField: "address", autoHighlight: false, callbackSelectedItem: (item) => {
                                        // Parse selection and autofill all fields; street input shows only street
                                        try {
                                            const parsed = addresser.parseAddress((item === null || item === void 0 ? void 0 : item.address) || '');
                                            setValue('street', parsed.addressLine1 || '', {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            setValue('city', parsed.placeName || '', {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            setValue('state', parsed.stateAbbreviation || '', {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            setValue('zip', parsed.zipCode || '', {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            clearErrors(['street', 'city', 'state', 'zip']);
                                        }
                                        catch (_err) {
                                            setError('street', {
                                                type: 'manual',
                                                message: 'Unable to parse the selected address. Please try another.',
                                            });
                                        }
                                    }, onChange: (value) => {
                                        // Only update the address text field while typing and reset others
                                        setValue('street', value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        setValue('city', '', { shouldDirty: true });
                                        setValue('state', '', { shouldDirty: true });
                                        setValue('zip', '', { shouldDirty: true });
                                    }, dropdownStyle: { maxHeight: '200px', overflow: 'auto' } })),
                            errors.street && (React.createElement("p", { className: "text-xs text-red-500" }, errors.street.message))),
                        React.createElement("div", { className: "grid grid-cols-2 gap-3" },
                            React.createElement("div", { className: "grid gap-1" },
                                React.createElement(Label, { htmlFor: "city", className: "text-xs" }, "City"),
                                React.createElement(Input, { id: "city", value: watch('city'), onChange: (e) => {
                                        setValue('city', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    } }),
                                errors.city && (React.createElement("p", { className: "text-xs text-red-500" }, errors.city.message))),
                            React.createElement("div", { className: "grid gap-1" },
                                React.createElement(Label, { htmlFor: "state", className: "text-xs" }, "State"),
                                React.createElement(Input, { id: "state", value: watch('state'), onChange: (e) => {
                                        setValue('state', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    } }),
                                errors.state && (React.createElement("p", { className: "text-xs text-red-500" }, errors.state.message)))),
                        React.createElement("div", { className: "grid grid-cols-2 gap-3" },
                            React.createElement("div", { className: "grid gap-1" },
                                React.createElement(Label, { htmlFor: "zip", className: "text-xs" }, "Zip Code"),
                                React.createElement(Input, { id: "zip", value: watch('zip'), onChange: (e) => {
                                        setValue('zip', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    } }),
                                errors.zip && (React.createElement("p", { className: "text-xs text-red-500" }, errors.zip.message)))))),
                React.createElement("div", { className: "grid gap-2" },
                    React.createElement(Label, { htmlFor: "hubSpecialInstructions" }, "Special Instructions"),
                    React.createElement(Textarea, Object.assign({ id: "hubSpecialInstructions", placeholder: "Enter any special instructions or notes for this hub..." }, register('specialInstructions'), { className: "min-h-24" }))),
                React.createElement(DialogFooter, null,
                    React.createElement(Button, { type: "button", variant: "outline", onClick: onClose, disabled: isSubmitting }, "Cancel"),
                    React.createElement(Button, { type: "submit", disabled: isSubmitting }, isSubmitting ? 'Adding hub...' : 'Add Hub'))))));
}
